import React, { useState, useRef } from 'react';
import { Field } from 'react-final-form';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from '../../../../../util/reactIntl';
import { useFormState, useForm } from 'react-final-form';

import { OutsideClickHandler, IconArrowHead } from '../../../../../components';

import css from './FilterPrice.module.css';
import FieldSelectIntegerRange from '../../../../SearchPage/IntegerRangeFilter/FieldSelectIntegerRange';

const PriceDropdown = ({
  input,
  className,
  rootClassName,
  alignLeft,
  onCountListingsByFilters,
  queryParams,
  setQueryParams,
  priceConfig,
  currencySymbol,
  dropdownOpenUpwardClass,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const intl = useIntl();

  const { values } = useFormState();
  const form = useForm();

  const priceValue = values?.price;

  const hasSelected =
    priceValue &&
    (priceValue.minValue != null || priceValue.maxValue != null);

  const handleApply = () => {
    if (!hasSelected) return;

    const cloneQueryParams = structuredClone(queryParams);
    cloneQueryParams.price = priceValue;

    setQueryParams(cloneQueryParams);

    // Exclude selected category so category facet options stay visible
    const { pub_categoryLevel1, ...paramsWithoutCategory } = cloneQueryParams;
    onCountListingsByFilters({ queryParams: paramsWithoutCategory });

    input.onChange(priceValue);
    setIsOpen(false);
  };

  const handleClear = () => {
    const cloneQueryParams = structuredClone(queryParams);
    delete cloneQueryParams.price;

    setQueryParams(cloneQueryParams);

    const { pub_categoryLevel1, ...paramsWithoutCategory } = cloneQueryParams;
    onCountListingsByFilters({ queryParams: paramsWithoutCategory });

    form.change('price', null);
    input.onChange(null);
    setIsOpen(false);
  };

  const selectedPriceLabel = hasSelected
    ? `${currencySymbol}${priceValue.minValue ?? 0} – ${currencySymbol}${priceValue.maxValue ?? '∞'}`
    : null;

  const { max, min, step, key } = priceConfig || {};
  const rootClass = rootClassName || css.root;

  return (
    <OutsideClickHandler
      className={classNames(rootClass, className)}
      onOutsideClick={() => setIsOpen(false)}
    >
      <div className={css.dropdownContainer}>
        <div className={css.dropdownItemPlaceholder}>
          <FormattedMessage id="PageBuilder.SearchCTA.pricePlaceholderDescription" />
        </div>

        <div
          role="button"
          tabIndex={0}
          onClick={() => setIsOpen(v => !v)}
          className={classNames(css.toggleButton, {
            [css.unselected]: !hasSelected,
          })}
        >
          <span className={classNames(css.dropdownItem, hasSelected && css.dropdownItemSelected)}>
            {selectedPriceLabel || (
              <FormattedMessage id="PageBuilder.SearchCTA.pricePlaceholder" />
            )}
          </span>
          <span className={classNames(css.chevron, isOpen && css.isOpen)} >
            <IconArrowHead direction={isOpen ? "up" : "down"} />
          </span>
        </div>

        {isOpen && (
          <div
            className={classNames(css.dropdownContent, dropdownOpenUpwardClass, {
              [css.alignLeft]: alignLeft,
            })}
          >
            <FieldSelectIntegerRange
              min={min}
              max={max}
              step={step}
              name={key}
              intl={intl}
              initialValues={{ price: { minValue: priceValue?.minValue || min, maxValue: priceValue?.maxValue || max } }}
              isPriceFilter
            />

            <div className={css.actions}>
              <span
                className={css.clearButton}
                onClick={handleClear}
              >
                <FormattedMessage id="PageBuilder.SearchCTA.priceClear" />
              </span>

              <span
                className={css.applyButton}
                onClick={handleApply}
              >
                <FormattedMessage id="PageBuilder.SearchCTA.priceApply" />
              </span>
            </div>
          </div>
        )}
      </div>
    </OutsideClickHandler>
  );
};


// Final Form wrapper
const FilterPrice = props => {
  const { className, rootClassName, ...rest } = props;
  return (
    <Field
      {...rest}
      name="price"
      component={PriceDropdown}
      className={className}
      rootClassName={rootClassName}
    />
  );
};

export default FilterPrice;
